import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Button,
  Text,
  Box,
  HStack,
  VStack,
  Spinner,
  Badge,
  Divider,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { CheckCircle2, Mail, PhoneCall, Ticket, Trash2 } from 'lucide-react'
import { apiClient } from '@/lib/api/client'
import { sanitizeEmailHtml } from '@/lib/utils/emailSafety'
import { acknowledgeTicketRequest } from '@/lib/services/ticketRequestAcknowledge'
import { getTicketRequestSourceEmail } from '@/lib/services/ticketRequestService'
import { buildTicketRequestSubject } from '@/lib/utils/ticketRequestSubject'
import { useTicketOptions } from '@/hooks/useTicketOptions'
import { useThemeColors } from '@/hooks/useThemeColors'
import { RequestSourceBadge } from '@/components/helpdesk/Cell/RequestSourceBadge'
import { ALICE_CALL_OUT_ENABLED } from '@/lib/vicidial'
import { openVicidialCall } from '@/lib/vicidialCallIntent'

type Revision = {
  id: number
  action: string
  changes?: Record<string, unknown>
  notes?: string | null
  user_name?: string | null
  created_at?: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  row: Record<string, unknown> | null
  onComposeEmail?: (row: Record<string, unknown>) => void
  onCallOut?: (row: Record<string, unknown>) => void
  onAcknowledged?: (payload: {
    parentTicketId?: number
    parentTicketNumber?: string | null
    createTicket: boolean
  }) => void
  onCreateTicket?: (row: Record<string, unknown>) => void
  /** Soft-delete (move to trash) or permanent delete; implemented by the parent. */
  showDelete?: boolean
  deleteLabel?: string
  onDelete?: () => void
  isDeleting?: boolean
  /** Trash / archived view: read-only, no triage actions. */
  readOnly?: boolean
}

function formatAction(action: string): string {
  return action.replace(/_/g, ' ')
}

function formatDateTime(value: unknown): string {
  if (!value || typeof value !== 'string') return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
}

function normalizeMessage(value: unknown): string {
  return String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function MetadataItem({ label, children }: { label: string; children: ReactNode }) {
  const colors = useThemeColors()
  return (
    <Box minW={0}>
      <Text fontSize="10px" color={colors.textMuted} fontWeight="600" mb={0.5} textTransform="uppercase" letterSpacing="wide">
        {label}
      </Text>
      <Box fontSize="12px" color={colors.text} noOfLines={1}>
        {children}
      </Box>
    </Box>
  )
}

export function TicketRequestTriageDrawer({
  isOpen,
  onClose,
  row,
  onComposeEmail,
  onCallOut,
  onAcknowledged,
  onCreateTicket,
  showDelete = false,
  deleteLabel = 'Delete',
  onDelete,
  isDeleting = false,
  readOnly = false,
}: Props) {
  const colors = useThemeColors()
  const toast = useToast()
  const { options: severityOptions, loading: severityLoading } = useTicketOptions('severity')
  const [loading, setLoading] = useState(false)
  const [acknowledging, setAcknowledging] = useState(false)
  const [createParentTicket, setCreateParentTicket] = useState(true)
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure()
  const [revisions, setRevisions] = useState<Revision[]>([])
  const [sourceEmailHtml, setSourceEmailHtml] = useState<string | null>(null)
  const [detailMeta, setDetailMeta] = useState<{
    source?: string
    label?: string
    clientName?: string
    submittedAt?: string
    acknowledgedAt?: string
    acknowledgedByName?: string
    acknowledgeDelayMinutes?: number
    parentTicketId?: number
    parentTicketNumber?: string
  }>({})

  const [form, setForm] = useState({
    subject: '',
    description: '',
    review_notes: '',
    contact_name: '',
    contact_email: '',
    contact_number: '',
    priority: 3,
  })

  const requestId = row?.id as number | undefined
  const isConverted = !!(detailMeta.parentTicketId ?? row?.parent_ticket_id)
  const isAcknowledged = !!(detailMeta.acknowledgedAt ?? row?.acknowledged_at)
  const canEdit = !readOnly && !isConverted && !isAcknowledged
  const showCreateTicket = !readOnly && isAcknowledged && !isConverted && !!onCreateTicket
  const showAckActions = !readOnly && !isAcknowledged && !isConverted
  // Wave A: always offer Vicidial Call out on non-trash; ALICE stays optional via flag + onCallOut
  const showCommunication = !readOnly

  const loadDetail = useCallback(async () => {
    if (!requestId) return
    setLoading(true)
    try {
      const [detailRes, revRes] = await Promise.all([
        apiClient.get(`/support/my-request/${requestId}`),
        apiClient.get(`/support/my-request/${requestId}/revisions`),
      ])
      const data = (detailRes.data?.data ?? detailRes.data) as Record<string, unknown>
      const subject = buildTicketRequestSubject({
        subject: data.subject as string | null | undefined,
        email_subject: data.email_subject as string | null | undefined,
        request_number: (data.request_number ?? row?.request_number) as string | null | undefined,
        id: (data.id ?? row?.id) as string | number | null | undefined,
        contact_name: data.contact_name as string | null | undefined,
        client_name: (data.client_name ?? row?.client_name) as string | null | undefined,
        extracted_fields: data.extracted_fields as Record<string, unknown> | null | undefined,
        description: data.description as string | null | undefined,
      })
      setForm({
        subject,
        description: String(data.description ?? ''),
        review_notes: String(data.review_notes ?? ''),
        contact_name: String(data.contact_name ?? ''),
        contact_email: String(data.contact_email ?? ''),
        contact_number: String(data.contact_number ?? ''),
        priority: Number(data.priority ?? 3),
      })
      setRevisions((revRes.data?.data ?? []) as Revision[])
      setDetailMeta({
        source: data.request_source != null ? String(data.request_source) : undefined,
        label: data.source_label != null ? String(data.source_label) : undefined,
        clientName: String(data.client_name ?? row?.client_name ?? ''),
        submittedAt: data.submitted_at != null ? String(data.submitted_at) : undefined,
        acknowledgedAt: data.acknowledged_at != null ? String(data.acknowledged_at) : undefined,
        acknowledgedByName:
          data.acknowledged_by_name != null ? String(data.acknowledged_by_name) : undefined,
        acknowledgeDelayMinutes:
          data.acknowledge_delay_minutes != null ? Number(data.acknowledge_delay_minutes) : undefined,
        parentTicketId: data.parent_ticket_id != null ? Number(data.parent_ticket_id) : undefined,
        parentTicketNumber:
          data.parent_ticket_number != null ? String(data.parent_ticket_number) : undefined,
      })

      if (data.request_source === 'email' || data.request_channel === 'email') {
        try {
          const emailRes = await getTicketRequestSourceEmail(requestId)
          setSourceEmailHtml(emailRes?.body_html ?? emailRes?.body ?? null)
        } catch {
          setSourceEmailHtml(null)
        }
      } else {
        setSourceEmailHtml(null)
      }
    } catch {
      toast({ title: 'Failed to load request', status: 'error' })
    } finally {
      setLoading(false)
    }
  }, [requestId, row?.request_number, row?.id, row?.client_name, toast])

  useEffect(() => {
    if (isOpen && requestId) {
      setCreateParentTicket(true)
      void loadDetail()
    } else {
      setDetailMeta({})
      setSourceEmailHtml(null)
    }
  }, [isOpen, requestId, loadDetail])

  const handleSaveAndAcknowledgeClick = () => {
    if (createParentTicket) {
      onConfirmOpen()
      return
    }
    void handleSaveAndAcknowledge()
  }

  const handleConfirmSaveAndAcknowledge = () => {
    onConfirmClose()
    void handleSaveAndAcknowledge()
  }

  const handleSaveAndAcknowledge = async () => {
    if (!requestId) return
    setAcknowledging(true)
    try {
      if (canEdit) {
        await apiClient.patch(`/support/my-request/${requestId}/review`, form)
      }
      const { parentTicket: parent } = await acknowledgeTicketRequest(requestId, createParentTicket)
      const parentId = parent?.id as number | undefined
      const ticketNumber =
        (parent?.ticket_number as string | undefined) ??
        (parent?.ticketNumber as string | undefined) ??
        null
      toast({
        title: 'Request acknowledged',
        description:
          createParentTicket && ticketNumber
            ? `Ticket ${ticketNumber} created.`
            : 'Create a ticket from the Acknowledged tab when ready.',
        status: 'success',
        duration: 5000,
      })
      onAcknowledged?.({
        parentTicketId: parentId,
        parentTicketNumber: ticketNumber,
        createTicket: createParentTicket,
      })
      onClose()
    } catch {
      toast({ title: 'Save & acknowledge failed', status: 'error' })
    } finally {
      setAcknowledging(false)
    }
  }

  const headerTitle = useMemo(() => {
    if (!row) return 'Review request'
    return String(row.request_number ?? requestId ?? '')
  }, [row, requestId])

  const requestSource = detailMeta.source ?? (row?.request_source as string | undefined)
  const requestSourceLabel = detailMeta.label ?? (row?.source_label as string | undefined)
  const clientName =
    detailMeta.clientName ||
    (row?.client_name as string | undefined) ||
    (row?.contact_name as string | undefined) ||
    '—'
  const submittedAt = formatDateTime(detailMeta.submittedAt ?? row?.submitted_at)
  const acknowledgedAtDisplay = formatDateTime(detailMeta.acknowledgedAt ?? row?.acknowledged_at)
  const acknowledgedByName =
    detailMeta.acknowledgedByName?.trim() ||
    (row?.acknowledged_by_name as string | undefined)?.trim() ||
    '—'
  const acknowledgeDelayMinutes =
    detailMeta.acknowledgeDelayMinutes ??
    (row?.acknowledge_delay_minutes as number | null | undefined) ??
    0
  const hasContactEmail = Boolean(form.contact_email?.trim() || row?.contact_email)
  const hasContactNumber = Boolean(form.contact_number?.trim() || row?.contact_number)
  const vicidialContactNumber = form.contact_number?.trim() || String(row?.contact_number ?? '')

  const handleOpenPhone = () => {
    if (!requestId) return
    openVicidialCall({
      requestId,
      requestLabel: String(row?.request_number ?? `Request ${requestId}`),
      contactName: form.contact_name?.trim() || String(row?.contact_name ?? 'Requestor'),
      contactNumber: vicidialContactNumber,
      placement: 'left',
    })
  }
  const originalMessageText = String(row?.raw_message ?? row?.description ?? '')
  const showOriginalMessage =
    Boolean(sourceEmailHtml) ||
    (normalizeMessage(originalMessageText) !== '' &&
      normalizeMessage(originalMessageText) !== normalizeMessage(form.description))

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg" placement="right" useInert={false} trapFocus={false}>
      <DrawerOverlay />
      <DrawerContent
        aria-modal={false}
        maxW={{ base: '100%', lg: 'min(820px, 58vw)' }}
        display="flex"
        flexDirection="column"
        maxH="100dvh"
      >
        <DrawerCloseButton size="sm" top={2} right={2} />
        <DrawerHeader flexShrink={0} borderBottomWidth="1px" py={2.5} px={4} pr={10}>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
              <HStack align="baseline" spacing={2} minW={0}>
                <Text fontSize="sm" fontWeight="semibold">
                  Review request
                </Text>
                <Text fontSize="xs" color={colors.textSecondary} fontWeight="500">
                  {headerTitle}
                </Text>
              </HStack>
              <HStack flexWrap="wrap" gap={1}>
                {readOnly ? (
                  <Badge colorScheme="red" variant="subtle" fontSize="10px" px={1.5}>
                    Trashed
                  </Badge>
                ) : null}
                {row?.needs_human_review ? (
                  <Badge colorScheme="orange" variant="subtle" fontSize="10px" px={1.5}>
                    Needs review
                  </Badge>
                ) : null}
                <Badge colorScheme={isAcknowledged ? 'green' : 'gray'} variant="outline" fontSize="10px" px={1.5}>
                  {isAcknowledged ? 'Acknowledged' : 'Pending'}
                </Badge>
                {isConverted ? (
                  <Badge colorScheme="blue" variant="outline" fontSize="10px" px={1.5}>
                    Ticket created
                  </Badge>
                ) : null}
              </HStack>
            </HStack>

            <SimpleGrid columns={{ base: 2, md: 4 }} gap={2} columnGap={3}>
              <MetadataItem label="Source">
                {requestSource ? (
                  <RequestSourceBadge source={requestSource} label={requestSourceLabel} />
                ) : (
                  <Text color={colors.textMuted}>—</Text>
                )}
              </MetadataItem>
              <MetadataItem label="Client">{clientName}</MetadataItem>
              <MetadataItem label="Submitted">{submittedAt}</MetadataItem>
              <MetadataItem label="Contact">
                {form.contact_name || (row?.contact_name as string | undefined) || '—'}
              </MetadataItem>
            </SimpleGrid>

            {isAcknowledged ? (
              <SimpleGrid columns={{ base: 2, md: 3 }} gap={2} columnGap={3} mt={2}>
                <MetadataItem label="Acknowledged At">{acknowledgedAtDisplay}</MetadataItem>
                <MetadataItem label="Acknowledged By">{acknowledgedByName}</MetadataItem>
                <MetadataItem label="Ack SLA">
                  {acknowledgeDelayMinutes > 0 ? (
                    <Badge colorScheme="orange" fontSize="10px">
                      {acknowledgeDelayMinutes} min late
                    </Badge>
                  ) : (
                    <Badge colorScheme="green" fontSize="10px">
                      On time
                    </Badge>
                  )}
                </MetadataItem>
              </SimpleGrid>
            ) : null}
          </VStack>
        </DrawerHeader>

        {showCommunication && row ? (
          <Box
            px={4}
            py={2}
            borderBottomWidth="1px"
            borderColor={colors.borderLight}
            bg="blue.50"
            _dark={{ bg: 'blue.900' }}
            flexShrink={0}
          >
            <HStack flexWrap="wrap" gap={2} align="center" justify="space-between">
              <HStack flexWrap="wrap" gap={2}>
              {onComposeEmail ? (
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="solid"
                  leftIcon={<Mail size={14} />}
                  onClick={() =>
                    onComposeEmail({
                      ...row,
                      contact_email: form.contact_email?.trim() || row.contact_email,
                      contact_name: form.contact_name?.trim() || row.contact_name,
                    })
                  }
                  isDisabled={!hasContactEmail}
                  title={!hasContactEmail ? 'No contact email on this request' : undefined}
                  boxShadow="sm"
                  h={8}
                  fontSize="xs"
                >
                  Compose Email
                </Button>
              ) : null}
              {ALICE_CALL_OUT_ENABLED && onCallOut ? (
                <Button
                  size="sm"
                  leftIcon={<PhoneCall size={14} />}
                  colorScheme="purple"
                  variant="outline"
                  onClick={() => onCallOut(row)}
                  h={8}
                  fontSize="xs"
                >
                  ALICE
                </Button>
              ) : null}
              <Button
                size="sm"
                leftIcon={<PhoneCall size={14} />}
                colorScheme="green"
                variant="solid"
                isDisabled={!hasContactNumber || !requestId}
                onClick={handleOpenPhone}
                h={8}
                fontSize="xs"
                title={hasContactNumber ? undefined : 'Contact phone required to call out'}
              >
                Call out
              </Button>
              </HStack>
              <Text fontSize="10px" color={colors.textSecondary} display={{ base: 'none', md: 'block' }}>
                Call controls open in Phone
              </Text>
            </HStack>
          </Box>
        ) : null}

        <DrawerBody flex="1" minH={0} overflowY="auto" px={4} py={3}>
          {loading ? (
            <HStack justify="center" py={6}>
              <Spinner size="sm" />
            </HStack>
          ) : (
            <>
              <SimpleGrid columns={{ base: 1, lg: showOriginalMessage ? 2 : 1 }} gap={3} alignItems="start">
                {showOriginalMessage ? (
                <VStack align="stretch" spacing={1.5} minW={0}>
                  <Text fontSize="11px" fontWeight="700" color={colors.textSecondary} textTransform="uppercase">
                    Original message
                  </Text>
                  {sourceEmailHtml ? (
                    <Box
                      borderWidth="1px"
                      borderColor={colors.borderLight}
                      borderRadius="md"
                      p={2}
                      maxH={{ base: '180px', lg: '320px' }}
                      overflowY="auto"
                      fontSize="12px"
                      lineHeight="1.45"
                      bg="white"
                      sx={{ '& *': { fontSize: '12px !important', lineHeight: '1.45 !important' } }}
                      dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(sourceEmailHtml) }}
                    />
                  ) : (
                    <Box
                      borderWidth="1px"
                      borderColor={colors.borderLight}
                      borderRadius="md"
                      p={2}
                      maxH={{ base: '180px', lg: '320px' }}
                      overflowY="auto"
                      fontSize="12px"
                      lineHeight="1.45"
                      whiteSpace="pre-wrap"
                      bg="white"
                    >
                      {originalMessageText || '—'}
                    </Box>
                  )}
                  {row?.confidence_score != null && (
                    <Text fontSize="10px" color={colors.textMuted}>
                      AI confidence: {String(row.confidence_score)}%
                    </Text>
                  )}
                </VStack>
                ) : null}

                <VStack align="stretch" spacing={2} minW={0}>
                  <Text fontSize="11px" fontWeight="700" color={colors.textSecondary} textTransform="uppercase">
                    Enriched request {canEdit ? '(editable)' : '(locked)'}
                  </Text>
                  <FormControl>
                    <FormLabel fontSize="11px" mb={0.5}>Subject</FormLabel>
                    <Input
                      size="xs"
                      fontSize="12px"
                      value={form.subject}
                      isDisabled={!canEdit}
                      placeholder="Request ID | Contact Name | Client"
                      onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="11px" mb={0.5}>Description</FormLabel>
                    <Textarea
                      size="xs"
                      fontSize="12px"
                      rows={showOriginalMessage ? 4 : 5}
                      value={form.description}
                      isDisabled={!canEdit}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="11px" mb={0.5}>Contact name</FormLabel>
                    <Input
                      size="xs"
                      fontSize="12px"
                      value={form.contact_name}
                      isDisabled={!canEdit}
                      onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="11px" mb={0.5}>Email</FormLabel>
                    <Input
                      size="xs"
                      fontSize="12px"
                      type="email"
                      value={form.contact_email}
                      isDisabled={!canEdit}
                      onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="11px" mb={0.5}>Phone</FormLabel>
                    <Input
                      size="xs"
                      fontSize="12px"
                      value={form.contact_number}
                      isDisabled={!canEdit}
                      onChange={(e) => setForm((f) => ({ ...f, contact_number: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="11px" mb={0.5}>Priority</FormLabel>
                    <Select
                      size="xs"
                      fontSize="12px"
                      value={form.priority}
                      isDisabled={!canEdit || severityLoading}
                      onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) }))}
                    >
                      {severityOptions.map((o) => (
                        <option key={o.code} value={o.code}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="11px" mb={0.5}>Review notes</FormLabel>
                    <Textarea
                      size="xs"
                      fontSize="12px"
                      rows={2}
                      value={form.review_notes}
                      isDisabled={!canEdit}
                      onChange={(e) => setForm((f) => ({ ...f, review_notes: e.target.value }))}
                    />
                  </FormControl>
                </VStack>
              </SimpleGrid>

              <Divider my={3} borderColor={colors.borderLight} />
              <Text fontSize="11px" fontWeight="700" mb={1.5} color={colors.textSecondary} textTransform="uppercase">
                Activity
              </Text>
              <VStack align="stretch" spacing={1} maxH="130px" overflowY="auto">
                {revisions.length === 0 ? (
                  <Text fontSize="11px" color={colors.textMuted}>
                    No activity yet
                  </Text>
                ) : (
                  revisions.map((rev) => (
                    <Box key={rev.id} fontSize="11px" borderWidth="1px" borderRadius="sm" px={2} py={1} borderColor={colors.borderLight}>
                      <HStack justify="space-between">
                        <Text fontWeight="medium">{formatAction(rev.action)}</Text>
                        <Text fontSize="10px" color={colors.textMuted}>
                          {rev.created_at ? new Date(rev.created_at).toLocaleString() : ''}
                        </Text>
                      </HStack>
                      <Text fontSize="10px" color={colors.textSecondary}>
                        {rev.user_name ?? 'System'}
                        {rev.notes ? ` — ${rev.notes}` : ''}
                      </Text>
                    </Box>
                  ))
                )}
              </VStack>
            </>
          )}
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" gap={2} flexShrink={0} flexWrap="wrap" px={4} py={2.5}>
          <HStack justify="space-between" w="100%" align="flex-start" flexWrap="wrap" spacing={2}>
            <VStack align="stretch" spacing={2} flex="1" minW="200px">
              {showAckActions ? (
                <VStack align="stretch" spacing={2} w="100%">
                  <Checkbox
                    size="sm"
                    colorScheme="blue"
                    isChecked={createParentTicket}
                    onChange={(e) => setCreateParentTicket(e.target.checked)}
                  >
                    <Text fontSize="xs">Also create parent ticket on acknowledge</Text>
                  </Checkbox>
                  <Text fontSize="xs" color="gray.500">
                    Email intake never creates a parent ticket or task. This option runs only when you acknowledge.
                  </Text>
                  <Button
                    size="sm"
                    fontSize="xs"
                    h={8}
                    colorScheme="blue"
                    leftIcon={<CheckCircle2 size={14} />}
                    onClick={() => void handleSaveAndAcknowledgeClick()}
                    isLoading={acknowledging}
                    isDisabled={isDeleting}
                    alignSelf="flex-start"
                  >
                    Save & Acknowledge
                  </Button>
                </VStack>
              ) : null}
              {showCreateTicket && row ? (
                <Button
                  size="sm"
                  fontSize="xs"
                  h={8}
                  colorScheme="green"
                  leftIcon={<Ticket size={14} />}
                  isDisabled={isDeleting}
                  onClick={() =>
                    onCreateTicket?.({
                      ...row,
                      ...form,
                      acknowledged_at: detailMeta.acknowledgedAt ?? row.acknowledged_at,
                      parent_ticket_id: detailMeta.parentTicketId ?? row.parent_ticket_id,
                    })
                  }
                  alignSelf="flex-start"
                >
                  Create Ticket
                </Button>
              ) : null}
              {isConverted ? (
                <Text fontSize="sm" color={colors.textSecondary}>
                  Ticket:{' '}
                  {String(detailMeta.parentTicketNumber ?? row?.parent_ticket_number ?? '—')}
                </Text>
              ) : null}
            </VStack>
            {showDelete && onDelete ? (
              <Button
                size="sm"
                fontSize="xs"
                h={8}
                colorScheme="red"
                variant={readOnly ? 'solid' : 'outline'}
                leftIcon={<Trash2 size={14} />}
                onClick={onDelete}
                isLoading={isDeleting}
                isDisabled={isDeleting || acknowledging}
                alignSelf="flex-end"
              >
                {deleteLabel}
              </Button>
            ) : null}
          </HStack>
        </DrawerFooter>
      </DrawerContent>

      <Modal
        isOpen={isConfirmOpen}
        onClose={acknowledging ? () => {} : onConfirmClose}
        isCentered
        closeOnOverlayClick={!acknowledging}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save & Acknowledge</ModalHeader>
          <ModalBody>
            <Text fontSize="sm">
              This will acknowledge the request and create a parent ticket. The request timer will stop
              and you will be taken to the new ticket.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onConfirmClose} isDisabled={acknowledging}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => void handleConfirmSaveAndAcknowledge()}
              isLoading={acknowledging}
            >
              Confirm & Acknowledge
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Drawer>
  )
}
